import { supabase } from '../lib/supabase';

export type QueryIntentType = 'THEORY' | 'CANON' | 'SHIP' | 'CONTROVERSY' | 'MEME' | 'CHARACTER' | 'GENERAL';
export type Platform = 'reddit' | 'twitter' | 'wiki' | 'ao3' | 'youtube';

export interface QueryIntent {
  type: QueryIntentType;
  confidence: number;
  searchTerms: string[];
  platformPriority: Platform[];
  fandom?: string;
  reasoning?: string;
}

// Platform priorities for each intent type (kept for reference, but handled by edge function)
const platformPriorities: Record<QueryIntentType, Platform[]> = {
  THEORY: ['reddit', 'youtube', 'twitter'],
  CANON: ['wiki', 'reddit'],
  SHIP: ['ao3', 'twitter', 'reddit'],
  CONTROVERSY: ['reddit', 'twitter', 'youtube'],
  MEME: ['reddit', 'twitter', 'youtube'],
  CHARACTER: ['wiki', 'reddit', 'youtube'],
  GENERAL: ['reddit', 'wiki', 'youtube']
};

export async function analyzeQueryIntent(query: string): Promise<QueryIntent> {
  try {
    const { data, error } = await supabase.functions.invoke('query-analyzer', {
      body: { query }
    });

    if (error) {
      console.error('Edge function error, using fallback:', error);
      return fallbackAnalysis(query);
    }

    return data as QueryIntent;
  } catch (error) {
    console.error('Query analysis failed, using fallback:', error);
    return fallbackAnalysis(query);
  }
}

// Simple fallback if edge function fails
function fallbackAnalysis(query: string): QueryIntent {
  const lowerQuery = query.toLowerCase();
  let type: QueryIntentType = 'GENERAL';
  
  if (lowerQuery.includes('explain') || lowerQuery.includes('theory') || lowerQuery.includes('really happened')) {
    type = 'THEORY';
  } else if (lowerQuery.includes('ship') || lowerQuery.includes('romance')) {
    type = 'SHIP';
  } else if (lowerQuery.includes('meme') || lowerQuery.includes('joke')) {
    type = 'MEME';
  } else if (lowerQuery.includes('controversy') || lowerQuery.includes('drama')) {
    type = 'CONTROVERSY';
  } else if (lowerQuery.includes('character') || lowerQuery.includes('backstory')) {
    type = 'CHARACTER';
  }
  
  return {
    type,
    confidence: 0.5,
    searchTerms: extractSearchTerms(query),
    platformPriority: platformPriorities[type]
  };
}

function extractSearchTerms(query: string): string[] {
  const commonWords = ['the', 'is', 'are', 'what', 'why', 'how', 'when', 'where', 'who', 'can', 'you', 'explain'];
  return query.split(/\s+/)
    .filter(word => word.length > 2 && !commonWords.includes(word.toLowerCase()))
    .slice(0, 5);
}

// Keep the platform query builder for search service
export function buildPlatformQuery(baseQuery: string, platform: Platform, intent: QueryIntent): string {
  const fandomSuffix = intent.fandom ? ` ${intent.fandom}` : '';
  const antiSpam = ' -"money secrets" -"grow your bank" -"save hundreds" -sponsored -advertisement';
  
  switch (platform) {
    case 'reddit':
      if (intent.type === 'THEORY') return `${baseQuery} theory discussion${fandomSuffix}`;
      if (intent.type === 'CONTROVERSY') return `${baseQuery} drama discourse${fandomSuffix}`;
      return `${baseQuery}${fandomSuffix} reddit`;
      
    case 'twitter':
      if (intent.type === 'SHIP') return `${baseQuery} ship thread${fandomSuffix}`;
      if (intent.type === 'MEME') return `${baseQuery} meme viral${fandomSuffix}`;
      return `${baseQuery}${fandomSuffix} twitter`;
      
    case 'wiki':
      return `${baseQuery} ${intent.fandom || ''} fandom wiki${antiSpam}`;
      
    case 'ao3':
      return `${baseQuery} fanfiction ao3`;
      
    case 'youtube':
      if (intent.type === 'THEORY') return `${baseQuery} theory explained${fandomSuffix}`;
      if (intent.type === 'CHARACTER') return `${baseQuery} character analysis${fandomSuffix}`;
      return `${baseQuery}${fandomSuffix} explained`;
      
    default:
      return baseQuery + antiSpam;
  }
}